/**
 * Scenario picker for the Plan timeline. Sits at the top of PlanPhase.
 *
 * A "scenario" is a copy of the plan you can edit without affecting
 * what the rest of the team sees. When you're happy, you can "make
 * it the plan" — that wholesale-replaces the current plan with your
 * scenario. Otherwise you can discard it.
 *
 * Internally these are still 'branches' in the DB / API for brevity;
 * only the user-facing copy says 'scenario'.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { IconFork, IconCheck, IconTrash, IconChevronRight } from './Icon';
import { Modal } from './Modal';
import { Avatar } from './Avatar';
import { UiNode } from '../lib/UiNode';
import type { BoardDetail, User } from '../../shared/types';

export interface BranchSummary {
  id: string;
  name: string;
  parentId: string | null;
  createdBy: string;
  description: string | null;
  colorHue: number;
  createdAt: number;
}

export function BranchSelector({
  board,
  activeBranchId,
  onChange,
  onMerged,
}: {
  board: BoardDetail;
  activeBranchId: string | null;
  onChange: (id: string | null) => void;
  onMerged: () => void;
}) {
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [forkOpen, setForkOpen] = useState(false);
  const [combineMode, setCombineMode] = useState(false);
  const [combineSel, setCombineSel] = useState<Set<string | null>>(new Set());
  const [combineName, setCombineName] = useState('');
  const [busy, setBusy] = useState(false);
  const isOwner = board.role === 'owner';

  function toggleCombine(id: string | null) {
    setCombineSel((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function combineNow() {
    if (combineSel.size < 2 || !combineName.trim()) return;
    setBusy(true);
    try {
      const r = await api.combineBranches(board.id, {
        sourceIds: [...combineSel],
        name: combineName.trim(),
      });
      await refresh();
      onChange(r.id);
      setCombineMode(false);
      setCombineSel(new Set());
      setCombineName('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function refresh(): Promise<void> {
    try {
      const r = await api.listBranches(board.id);
      setBranches(r.branches);
    } catch {
      /* noop */
    }
  }

  useEffect(() => {
    void refresh();
  }, [board.id]);

  const active = activeBranchId ? branches.find((b) => b.id === activeBranchId) : null;
  const activeAuthor = active ? board.members.find((m) => m.id === active.createdBy) ?? null : null;

  async function makeItThePlan(branchId: string) {
    if (
      !confirm(
        "Make this scenario the plan? The current plan will be replaced for everyone.",
      )
    )
      return;
    setBusy(true);
    try {
      await api.mergeBranch(board.id, branchId);
      await refresh();
      onChange(null);
      onMerged();
    } finally {
      setBusy(false);
    }
  }

  async function discard(branchId: string) {
    if (!confirm('Discard this scenario? You can\'t undo.')) return;
    setBusy(true);
    try {
      await api.deleteBranch(board.id, branchId);
      await refresh();
      if (activeBranchId === branchId) onChange(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <UiNode
          as="button"
          name="scenario-picker-button"
          entity="scenario"
          entityId={active?.id ?? 'plan'}
          action="picker_open"
          state={active ? 'scenario' : 'plan'}
          type="button"
          className="row-nudge inline-flex items-center gap-2 rounded-full border border-ink-rim bg-ink-3 px-3 py-1 text-xs"
          onClick={() => setOpen(true)}
          style={
            active
              ? {
                  borderColor: `hsla(${active.colorHue}, 70%, 55%, 0.7)`,
                  background: `linear-gradient(180deg, hsla(${active.colorHue}, 70%, 28%, 0.55), hsla(${active.colorHue}, 70%, 18%, 0.45))`,
                  boxShadow: `0 0 0 1px hsla(${active.colorHue}, 70%, 55%, 0.15), 0 8px 22px -14px hsla(${active.colorHue}, 70%, 50%, 0.65)`,
                }
              : undefined
          }
        >
          <IconFork size={12} />
          <span className="font-medium">
            {active ? `Scenario: ${active.name}` : 'The plan'}
          </span>
          {activeAuthor && (
            <span className="inline-flex items-center gap-1 text-[10px] text-cream-3">
              <Avatar user={activeAuthor} size={12} />
              {activeAuthor.name}
            </span>
          )}
          <IconChevronRight size={12} />
        </UiNode>
        {active && isOwner && (
          <UiNode
            as="button"
            name="scenario-make-plan-button"
            entity="scenario"
            entityId={active.id}
            action="make_plan"
            state={busy ? 'busy' : 'idle'}
            type="button"
            className="btn btn-primary inline-flex items-center gap-1.5 text-xs"
            onClick={() => makeItThePlan(active.id)}
            disabled={busy}
          >
            <IconCheck size={12} />
            Make this the plan
          </UiNode>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Plan & scenarios" maxWidth={520}>
        <div className="flex flex-col gap-2">
          {combineMode ? (
            <div className="mb-1 rounded-lg border border-clove/40 bg-clove/10 p-2 text-xs">
              Pick 2 or more to combine. The AI will fuse them into a new scenario.
            </div>
          ) : null}

          <BranchItem
            label="The plan"
            description="What everyone sees by default."
            active={activeBranchId === null}
            colorHue={null}
            onPick={() => {
              if (combineMode) toggleCombine(null);
              else {
                onChange(null);
                setOpen(false);
              }
            }}
            selectable={combineMode}
            selected={combineSel.has(null)}
          />
          {branches.map((b) => (
            <BranchItem
              key={b.id}
              label={b.name}
              description={b.description ?? null}
              active={activeBranchId === b.id}
              colorHue={b.colorHue}
              author={board.members.find((m) => m.id === b.createdBy) ?? null}
              onPick={() => {
                if (combineMode) toggleCombine(b.id);
                else {
                  onChange(b.id);
                  setOpen(false);
                }
              }}
              onArchive={combineMode ? undefined : () => discard(b.id)}
              selectable={combineMode}
              selected={combineSel.has(b.id)}
            />
          ))}

          {!combineMode ? (
            <>
              <UiNode
                as="button"
                name="scenario-create-open-button"
                entity="scenario"
                action="create_start"
                type="button"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-ink-rim px-3 py-2 text-sm text-cream-3 hover:border-cream-4"
                onClick={() => {
                  setOpen(false);
                  setForkOpen(true);
                }}
              >
                <IconFork size={14} />
                Try a scenario from {activeBranchId ? active?.name ?? 'this scenario' : 'the plan'}…
              </UiNode>
              {branches.length > 0 && (
                <UiNode
                  as="button"
                  name="scenario-combine-open-button"
                  entity="scenario"
                  action="combine_start"
                  type="button"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-ink-rim px-3 py-2 text-sm text-cream-3 hover:border-cream-4"
                  onClick={() => {
                    setCombineMode(true);
                    setCombineSel(new Set());
                  }}
                >
                  Combine scenarios…
                </UiNode>
              )}
              <p className="px-1 pt-1 text-[11px] text-cream-3">
                A scenario is a copy you can edit safely. When you're happy, make it the plan
                and your changes replace it for the team. Otherwise discard it — no harm done.
              </p>
            </>
          ) : (
            <div className="mt-2 flex flex-col gap-2 border-t border-ink-rim/60 pt-3">
              <label className="flex flex-col gap-1 text-xs text-cream-3">
                New scenario name
                <input
                  className="input text-sm"
                  value={combineName}
                  onChange={(e) => setCombineName(e.target.value)}
                  placeholder="Combined plan"
                  autoFocus
                />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setCombineMode(false);
                    setCombineSel(new Set());
                    setCombineName('');
                  }}
                  disabled={busy}
                >
                  Cancel
                </button>
                <UiNode
                  as="button"
                  name="scenario-combine-submit-button"
                  entity="scenario"
                  action="combine_confirm"
                  state={busy ? 'busy' : 'idle'}
                  type="button"
                  className="btn btn-primary inline-flex items-center gap-1.5"
                  onClick={combineNow}
                  disabled={busy || combineSel.size < 2 || !combineName.trim()}
                  title={
                    combineSel.size < 2
                      ? 'Pick at least 2 scenarios to combine.'
                      : 'AI will fuse them into a new scenario.'
                  }
                >
                  {busy ? 'Combining…' : `Combine ${combineSel.size}`}
                </UiNode>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ForkDialog
        open={forkOpen}
        onClose={() => setForkOpen(false)}
        parentName={active?.name ?? 'the plan'}
        onSubmit={async (name, description) => {
          setBusy(true);
          try {
            const r = await api.forkBranch(board.id, {
              name,
              description: description || undefined,
              parentBranchId: activeBranchId,
            });
            await refresh();
            onChange(r.id);
            setForkOpen(false);
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

function BranchItem({
  label,
  description,
  active,
  colorHue,
  author,
  onPick,
  onArchive,
  selectable,
  selected,
}: {
  label: string;
  description: string | null;
  active: boolean;
  colorHue: number | null;
  author?: Pick<User, 'id' | 'name' | 'avatarHue' | 'avatarUrl'> | null;
  onPick: () => void;
  onArchive?: () => void;
  selectable?: boolean;
  selected?: boolean;
}) {
  const borderColor = selectable
    ? selected
      ? 'var(--color-clove)'
      : 'var(--color-ink-rim)'
    : active
      ? 'var(--color-clove)'
      : 'var(--color-ink-rim)';
  return (
    <div
      className="row-nudge flex items-center gap-2 rounded-lg border bg-ink-3 p-2 hover:bg-ink-2"
      style={{ borderColor }}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onPick}
          aria-label={`Include ${label}`}
        />
      )}
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{
          background: colorHue != null ? `hsl(${colorHue}, 70%, 55%)` : 'var(--color-cream-3)',
        }}
      />
      <button type="button" className="min-w-0 flex-1 text-left" onClick={onPick}>
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{label}</span>
          {author && (
            <span className="inline-flex items-center gap-1 text-[11px] text-cream-3">
              <Avatar user={author} size={14} />
              {author.name}
            </span>
          )}
        </div>
        {description && <div className="truncate text-xs text-cream-3">{description}</div>}
      </button>
      {!selectable && active && <span className="chip">Viewing</span>}
      {onArchive && (
        <UiNode
          as="button"
          name="scenario-discard-button"
          entity="scenario"
          action="discard"
          type="button"
          className="btn btn-ghost p-1 text-rust"
          onClick={onArchive}
          aria-label="Discard scenario"
          title="Discard scenario"
        >
          <IconTrash size={12} />
        </UiNode>
      )}
    </div>
  );
}

function ForkDialog({
  open,
  onClose,
  parentName,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  parentName: string;
  onSubmit: (name: string, description: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} title={`Try a scenario`} maxWidth={460}>
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          try {
            await onSubmit(name.trim(), description.trim());
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-cream-3">
          We'll make a copy of {parentName} that only you and others looking at this scenario
          will see. Tweak it however you like; when you're ready you can make it the plan,
          or discard it and forget about it.
        </p>
        <label className="flex flex-col gap-1 text-xs text-cream-3">
          Name
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What if we moved keynote to Friday?"
            autoFocus
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-cream-3">
          What are you trying?
          <textarea
            className="input min-h-[60px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Tighter days, more AI focus, swap the keynote slot…"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <UiNode
            as="button"
            name="scenario-create-submit-button"
            entity="scenario"
            action="create_confirm"
            state={busy ? 'busy' : 'idle'}
            type="submit"
            className="btn btn-primary"
            disabled={busy || !name.trim()}
          >
            {busy ? 'Creating…' : 'Create scenario'}
          </UiNode>
        </div>
      </form>
    </Modal>
  );
}
